import React from 'react'
import { useFormikContext } from 'formik'
import { Card, CardContent, Grid, Stack, Typography, Chip, Alert, AlertTitle } from '@mui/material'
import NatureIcon from '@mui/icons-material/Nature'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ItemsContainer from '../../ItemsContainer'

const pickItems = (items, names) =>
  names.map(name => items.find(i => i.name === name)).filter(Boolean)

const SectionHeader = ({ icon: Icon, label, trailing }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ flex: 1 }}>
      {label}
    </Typography>
    {trailing}
  </Stack>
)

const TreeDataStep = ({ itemsGeneral, mode, treeData }) => {
  const { values, setFieldValue } = useFormikContext()

  const pendingItems = React.useMemo(() => {
    if (mode !== 'edit' || !treeData) return []
    const items = []
    if (treeData.specie?.family?.validationStatus === 'pendiente')
      items.push(`Familia: ${treeData.specie.family.name}`)
    if (treeData.specie?.validationStatus === 'pendiente')
      items.push(`Especie: ${treeData.specie.name}`)
    const pendingPseudonyms = (treeData.specie?.pseudonym ?? [])
      .filter(p => p.validationStatus === 'pendiente')
      .map(p => `Nombre Común: ${p.name}`)
    return [...items, ...pendingPseudonyms]
  }, [mode, treeData])

  const disabledItems = React.useMemo(() => {
    if (mode !== 'edit' || !treeData) return []
    const items = []
    if (treeData.specie?.family?.habilitado === false)
      items.push(`Familia: ${treeData.specie.family.name}`)
    if (treeData.specie?.habilitado === false)
      items.push(`Especie: ${treeData.specie.name}`)
    const disabled = (treeData.specie?.pseudonym ?? [])
      .filter(p => p.habilitado === false)
      .map(p => `Nombre Común: ${p.name}`)
    return [...items, ...disabled]
  }, [mode, treeData])

  const specieItems = React.useMemo(() => {
    const items = pickItems(itemsGeneral, ['family_', 'specie_'])
    if (mode !== 'edit' || !treeData) return items

    return items.map(item => {
      if (item.name === 'family_' && treeData.specie?.family?.habilitado === false) {
        const familyId = String(treeData.specie.family.id)
        if (!item.options?.[familyId]) {
          return { ...item, options: { ...(item.options || {}), [familyId]: treeData.specie.family.name } }
        }
      }
      if (item.name === 'specie_' && treeData.specie?.habilitado === false) {
        const specieId = String(treeData.specie.id)
        if (!item.options?.[specieId]) {
          return { ...item, options: { ...(item.options || {}), [specieId]: treeData.specie.name } }
        }
      }
      return item
    })
  }, [itemsGeneral, mode, treeData])
  const pseudonymItems = pickItems(itemsGeneral, ['pseudonym_']).map(item => {
    if (mode === 'edit' && treeData?.specie?.pseudonym?.length) {
      return {
        ...item,
        data: treeData.specie.pseudonym.map(p => ({ id: p.id, name: p.name })),
      }
    }
    return item
  })

  const isAlive = (values.alive_ ?? 1) === 1 || values.alive_ === '1'

  return (
    <Stack spacing={2.5}>
      {/* Species Identification + alive status */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader
            icon={NatureIcon}
            label="Identificación de Especie"
            trailing={
              <Stack direction="row" spacing={0.5}>
                <Chip
                  label="Vivo"
                  size="small"
                  color={isAlive ? 'success' : 'default'}
                  variant={isAlive ? 'filled' : 'outlined'}
                  onClick={() => setFieldValue('alive_', 1)}
                  icon={<FavoriteIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="Muerto"
                  size="small"
                  color={!isAlive ? 'error' : 'default'}
                  variant={!isAlive ? 'filled' : 'outlined'}
                  onClick={() => setFieldValue('alive_', 0)}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            }
          />
          {pendingItems.length > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
              <AlertTitle sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                Pendiente de validación
              </AlertTitle>
              {pendingItems.map((item, i) => (
                <Typography key={i} variant="caption" display="block">
                  {item}
                </Typography>
              ))}
            </Alert>
          )}
          {disabledItems.length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
              <AlertTitle sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                Deshabilitado
              </AlertTitle>
              {disabledItems.map((item, i) => (
                <Typography key={i} variant="caption" display="block">
                  {item}
                </Typography>
              ))}
            </Alert>
          )}
          <Grid container spacing={2}>
            {specieItems.map(item => (
              <ItemsContainer key={item.name} {...item} editable />
            ))}
            {pseudonymItems.map(item => (
              <ItemsContainer key={item.name} {...item} x={12} s={12} m={12} editable />
            ))}
          </Grid>
        </CardContent>
      </Card>

    </Stack>
  )
}

export default TreeDataStep
